import { Strategy, TimeFrame } from "../../../store/slices/strategySlice";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import formatNumber from "../../../utils/formatNumber";
import AutoStrategyLaunch from "./AutoStrategyLaunch";
import { TradingViewLaunchProps, UserState } from "../../../_app/types";
import { _roundDec } from "../../../utils/_roundToDecimals";
import Header from "../../../_modules/Header/Header";
import PageLayout from "../../../_components/PageLayout";
import Spacer from "../../../_components/Spacer";
import DottedTextField from "../../../_components/DottedTextField";
import BadgeDirection from "../../../_components/BadgeDirection";
import { TradingView } from "../../../components/ui/TradingView";

interface Props {
  showPreview: (value: boolean) => void;
  strategy: Strategy;
  middlePrice: number;
  timeFrames: TimeFrame[];
  term: "Short" | "Mid" | "Long";
  user: UserState;
  chartOptions: TradingViewLaunchProps;
}

const AutoStrategyPreviewPage = ({
  strategy,
  timeFrames,
  showPreview,
  term,
  user,
  middlePrice,
  chartOptions,
}: Props) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background:
          theme.palette.mode === "dark" ? "rgba(13, 16, 18, 1)" : "rgba(245, 245, 245, 1)",
      }}
    >
      <PageLayout
        header={<Header pageTitle={"Futures bot preview"} needBack onChange={showPreview} />}
        bottomButton={
          <AutoStrategyLaunch
            strategy={strategy}
            middlePrice={middlePrice}
            timeFrames={timeFrames}
            term={term}
            user={user}
          />
        }
        contentSx={{ padding: "20px 8px 0" }}
      >
        <Box>
          <Box sx={{ marginBottom: 3 }}>
            <TradingView
              background={theme.palette.background.default}
              headerColor={theme.palette.background.default}
              ChartProperties={chartOptions}
            />
          </Box>
          <Stack direction={"column"} alignItems={"center"} spacing={1} sx={{ marginTop: 2 }}>
            <Stack sx={{ width: "100%" }} direction={"column"} alignItems={"center"} spacing={2}>
              <Stack style={{ width: "100%" }} spacing={2} sx={{ marginTop: 2 }}>
                <Typography variant="h2" component="div" color={"textSecondary"}>
                  Strategy settings
                </Typography>
                <Stack
                  sx={{ width: "100%" }}
                  direction={"column"}
                  alignItems={"center"}
                  spacing={1}
                >
                  <DottedTextField placeholder={"Account"} value={user.activeAccount.name} />
                  <DottedTextField
                    placeholder={"Pair"}
                    value={`${strategy.base}/${strategy.quote}`}
                  />
                  <DottedTextField
                    placeholder={"Investment"}
                    value={`${strategy.investment} ${strategy.currencyType === "BASE" ? strategy.base : strategy.quote}`}
                  />
                  <DottedTextField
                    placeholder={"Required margin"}
                    value={`${formatNumber(
                      _roundDec(
                        (strategy.currencyType === "BASE"
                          ? strategy.investment * middlePrice
                          : strategy.investment) / strategy.leverage,
                        2
                      )
                    )} ${strategy.quote}`}
                  />
                  <DottedTextField
                    placeholder={"Direction"}
                    value={``}
                    children={<BadgeDirection gridDirection={strategy.gridDirection} />}
                  />
                  <DottedTextField
                    placeholder={"Low price"}
                    value={`${formatNumber(strategy.minPrice)} ${strategy.quote}`}
                  />
                  <DottedTextField
                    placeholder={"High price"}
                    value={`${formatNumber(strategy.maxPrice)} ${strategy.quote}`}
                  />
                  <DottedTextField
                    placeholder={"Timeframe settings"}
                    value={`${strategy.customFrame ? "Custom term" : term + " term"}`}
                  />
                  <DottedTextField
                    placeholder={"Position behaviour"}
                    value={`${strategy.positionBehaviour}`}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Spacer height="95px" />
        </Box>
      </PageLayout>
    </Box>
  );
};

export default AutoStrategyPreviewPage;
